import { CreateOrganizationsInput } from "@/graphql/__generated__/graphql";
import { nonEmptyString } from "@/utils/form";
import { z } from "zod";

// Recursive schema for child organizations
export const createChildOrganizationSchema = (t: any): z.ZodType<CreateOrganizationsInput> => {
  return z.lazy(() => z.object({
    organization: z.object({
      name: nonEmptyString(t("modals:createorganization.inputs.name.label")),
      shortName: nonEmptyString(t("modals:createorganization.inputs.shortname.label")),
      province: nonEmptyString(t("modals:createorganization.inputs.province.label")),
    }),
    parentId: z.number().nullable().optional(),
    children: z.array(createChildOrganizationSchema(t)).optional(),
  }));
};

export const createFormSchema = (t: any) => {
  const baseNumber = z.number({
    error: (issue) => issue.input === undefined ?
      t("schemas:form.required")
      :
      t("schemas:form.invalid"),
  });

  const nonNegativeInt = baseNumber.int().nonnegative();
  const nonNegativeNum = baseNumber.nonnegative();

  return z.object({
    input: z.object({
      parentId: z.number().nullable().optional(),
      organization: z.object({
        name: nonEmptyString(t("modals:createorganization.inputs.name.label")),
        shortName: nonEmptyString(t("modals:createorganization.inputs.shortname.label")),
        province: nonEmptyString(t("modals:createorganization.inputs.province.label")),
      }),
      children: z.array(createChildOrganizationSchema(t)).optional(),
    }),
    isMainOrganization: z.boolean().default(false),
    advancedCostParameters: z.object({
      averageHoursWorkedPerMonth: nonNegativeInt,
      averageLengthOfEmployment: nonNegativeInt,
      carCostsPerMinute: nonNegativeNum,
      employeeFluctuation: baseNumber.min(0).max(100, { message: t("schemas:form.atmax100") }),
      otherCostsPerMinute: nonNegativeNum,
      otherMaterialCosts: nonNegativeNum,
      sickDays: nonNegativeInt,
      trainingDays: nonNegativeInt,
      yearlyTrainingCosts: nonNegativeNum,
      yearlyUniformCosts: nonNegativeNum,
    }).optional(),
  }).refine((data) => {
    // If main organization, advancedCostParameters is required
    if (data.isMainOrganization) {
      return data.advancedCostParameters !== undefined;
    }
    return true;
  }, {
    message: t("modals:createorganization.validation.advancedcostsrequired"),
    path: ["advancedCostParameters"],
  }).refine((data) => {
    // If not main organization, parentId is required
    if (!data.isMainOrganization && (data.input.parentId === undefined || data.input.parentId === null)) {
      return false;
    }
    return true;
  }, {
    message: t("modals:createorganization.validation.parentrequired"),
    path: ["input", "parentId"],
  });
};

export const defaultFormValues = {
  input: {
    parentId: null,
    organization: {
      name: "",
      shortName: "",
      province: undefined,
    },
    children: undefined,
  },
  isMainOrganization: true,
  advancedCostParameters: undefined,
};

export type TCreateOrganizationFormSchema = z.infer<ReturnType<typeof createFormSchema>>;
